import {
  AddLanguage,
  Button,
  DefineMainLanguage,
  DeleteLanguage,
  Editor,
  IconButton,
  LanguageMultiSelect,
  Popup,
  Select,
  Typography,
} from '@/components';
import {
  FILTER_APP_TEXT_OPTIONS,
  FilterAppTextOption,
} from '@/constants/FilterAppTextOption';
import LANGUAGES from '@/constants/Languages';
import { useAppTexts } from '@/contexts/AppTextsContext';
import { useGlobal } from '@/contexts/GlobalContext';
import { useProject } from '@/contexts/ProjectContext';
import AppText, { appTextHasMissingValue } from '@/entities/AppText';
import { getEditorAppTexts } from '@/utils/editor';
import Language, { sortLanguagesByMain } from 'entities/Language';
import { useEffect, useState } from 'react';
import { LuListPlus, LuStar, LuTrash } from 'react-icons/lu';
import styles from './TranslationsView.module.css';

interface TranslationViewProps {}

const TranslationsView = ({}: TranslationViewProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedLanguages, setSelectedLanguages] = useState<Language[]>([]);
  const [filteredAppTexts, setFilteredAppTexts] = useState<AppText[]>([]);
  const [activeFilter, setActiveFilter] = useState<
    FilterAppTextOption | undefined
  >(FILTER_APP_TEXT_OPTIONS[0]);

  const { enableOptionSetTranslation, setEnableOptionSetTranslation } =
    useGlobal();

  const [addLanguageOpen, setAddLanguageOpen] = useState(false);
  const [deleteLanguageOpen, setDeleteLanguageOpen] = useState(false);
  const [defineMainLanguageOpen, setDefineMainLanguageOpen] = useState(false);

  const {
    project,
    setProject,
    deleteLanguage,
    updateMainLanguage,
    addLanguage,
  } = useProject();

  const { appTexts, setAppTexts } = useAppTexts();

  async function dataFetch() {
    setIsLoading(true);
    if (project) {
      const editorAppTexts = await getEditorAppTexts(
        enableOptionSetTranslation,
        project.languages,
        project.mainLanguage,
      );
      setAppTexts(editorAppTexts);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    let updatedSelectedLanguages: Language[] = [];
    if (project && selectedLanguages.length > 0) {
      updatedSelectedLanguages = selectedLanguages.filter(
        (selectedLanguage) => {
          return project.languages.some((language) => {
            return language.code === selectedLanguage.code;
          });
        },
      );
    } else {
      updatedSelectedLanguages = project?.languages || [];
    }
    setSelectedLanguages(updatedSelectedLanguages);
  }, [appTexts]);

  useEffect(() => {
    dataFetch();
  }, [project, enableOptionSetTranslation]);

  useEffect(() => {
    filter();
  }, [appTexts, activeFilter, selectedLanguages]);

  const filter = () => {
    switch (activeFilter?.id) {
      case 'all':
        setFilteredAppTexts(appTexts);
        break;
      case 'untranslated':
        setFilteredAppTexts(
          appTexts.filter((appText) =>
            appTextHasMissingValue(appText, selectedLanguages),
          ),
        );
        break;
      default:
        setFilteredAppTexts(appTexts);
    }
  };

  if (!project) {
    return <p>No project selected. Please create or select a project.</p>;
  }

  return (
    <div>
      <Popup
        open={defineMainLanguageOpen}
        title={'Define main language'}
        onClose={() => setDefineMainLanguageOpen(false)}
      >
        <DefineMainLanguage
          currentMainLanguage={project.mainLanguage}
          languages={!project.languages ? [] : project.languages}
          mainLanguageSelected={(language) => {
            updateMainLanguage(language);
            setDefineMainLanguageOpen(false);
          }}
        />
      </Popup>
      <Popup
        open={addLanguageOpen}
        title={'Add a language'}
        onClose={() => setAddLanguageOpen(false)}
      >
        <AddLanguage
          languages={LANGUAGES.filter((language) => {
            return !project.languages?.some((item) => {
              return item.code === language.code;
            });
          })}
          languageAdded={(language) => {
            addLanguage(language);
            setAddLanguageOpen(false);
          }}
        />
      </Popup>
      <Popup
        title={'Delete a language'}
        open={deleteLanguageOpen}
        onClose={() => setDeleteLanguageOpen(false)}
      >
        <DeleteLanguage
          languages={project.languages}
          languageDeleted={(deletedLanguage) => {
            deleteLanguage(deletedLanguage);
            setDeleteLanguageOpen(false);
          }}
        />
      </Popup>
      <div className={styles.header}>
        <div className={styles.title}>
          <Typography>App ID : {project.appId}</Typography>
        </div>
        <div className={styles.filters}>
          <div style={{ width: '180px' }}>
            <Select
              options={FILTER_APP_TEXT_OPTIONS}
              initialOption={activeFilter}
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option.id}
              onChange={(value) => setActiveFilter(value)}
              placeholder="Filter"
            />
          </div>
          <div style={{ width: '220px', flex: '1 0 auto' }}>
            <LanguageMultiSelect
              initialOptions={selectedLanguages}
              options={project?.languages || []}
              onChange={(selectedLanguages) => {
                const sortSelectedLanguages =
                  sortLanguagesByMain(selectedLanguages);
                setSelectedLanguages(sortSelectedLanguages);
              }}
              addLanguage={() => setAddLanguageOpen(true)}
              deleteLanguage={() => setDeleteLanguageOpen(true)}
              setMainLanguage={() => {}}
            />
          </div>

          <div>
            <IconButton
              onClick={() => setAddLanguageOpen(true)}
              icon={<LuListPlus />}
            />
          </div>
          <div>
            <IconButton
              onClick={() => setDeleteLanguageOpen(true)}
              icon={<LuTrash />}
            />
          </div>
          <div>
            <IconButton
              onClick={() => setDefineMainLanguageOpen(true)}
              icon={<LuStar />}
            />
          </div>
          <div>
            <Button label="Refresh" onClick={dataFetch} />
          </div>
        </div>
      </div>
      <Editor languages={selectedLanguages} appTexts={filteredAppTexts} />
    </div>
  );
};

export default TranslationsView;
